import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { MultiPageVideoModel } from '@bynder-studio/render-core';
import debounce from '@bynder-studio/render-core/src/Helpers/debounce';
import { Buffer } from '@bynder/design-system';
import ShotVideoDesign from 'packages/pages/design/sidebar/shots/ShotVideoDesign';
import ShotImageDesign from 'packages/pages/design/sidebar/shots/ShotImageDesign';
import PagePreview from 'packages/pages/design/components/PagePreview/index';
import useDesign from 'packages/pages/design/hooks/useDesign';
import useVariations from 'packages/pages/design/hooks/useVariations';
import usePageShortcuts from 'packages/hooks/shortcuts/usePageShortcuts';
import { useCanvasBorder } from 'packages/hooks/useCanvasBorder';
import { DesignTab } from 'packages/pages/design/sidebar/types';
import { PagePreviewProvider } from 'packages/contexts/PagePreviewContext';
import { DesignSelectedElementProvider } from 'packages/pages/design/DesignSelectedElementContext';
import useForceUpdate from 'packages/hooks/useForceUpdate';
import { pendingDesignSavingRequest } from 'packages/store/creatives/creatives.actions';
import CreativeTypes from 'packages/helpers/CreativeTypes';
import PreviewHeader from '../previewHeader/PreviewHeader';
import Sidebar from '../sidebar/Sidebar';
import ConfiguratorTimeline from '../timeline';
import CanvasZoomControls from 'packages/common/editor/CanvasZoomControls/CanvasZoomControls';
import { getVisibleElements } from './utils';
import {
    DesignBuffer,
    DesignCanvas,
    DesignCanvasBorder,
    DesignCanvasPaddingWrapper,
    DesignCanvasWrapper,
    DesignContainer,
    DesignMain,
    DesignSidebar,
    DesignWrapper,
    DesignZoomControlsWrapper,
} from './design.styled';
import { CollapseExpandDivider } from 'packages/common/CollapseExpandDivider';
import useVariationsShortcuts from 'packages/hooks/shortcuts/useVariationsShortcuts';
import useAccessRights from 'packages/hooks/useAccessRights';
import features from 'packages/configs/features';

export const Design = () => {
    const dispatch = useDispatch();
    const forceUpdate = useForceUpdate();
    const {
        isUpdatedByVariation,
        creativeModel,
        initCanvasRenderer,
        template,
        canvasRenderer,
        creativeType,
        setPausePlayback,
        setPlayPlayback,
        isPlaying,
        creativeModelFactory,
        setCurrentPageIndex,
    } = useDesign();

    const {
        variations = [],
        currentVariation,
        saveVariation,
        variationSaveInProgressMap,
        selectNextVariation,
        selectPreviousVariation,
    } = useVariations();
    const { isPlatformAllowed } = useAccessRights();
    const isDynamicElementSelectionEnabled = useMemo(
        () => isPlatformAllowed([features.DYNAMIC_ELEMENT_SELECTION]),
        [isPlatformAllowed],
    );

    const currentPageIndex = creativeModel.getCurrentPageIndex();
    const canvasWrapperRef = useRef(null);
    const borderElementRef = useRef<HTMLDivElement | null>(null);
    useCanvasBorder(canvasRenderer, borderElementRef);
    const [selectedShotIndex, setSelectedShotIndex] = useState(0);
    const [isToolbarDisabled, setIsToolbarDisabled] = useState(false);
    // todo: Shots should be stateless hook and share between all editors
    const [shots, setShots] = useState(() =>
        creativeModel instanceof MultiPageVideoModel ? creativeModel.getShots() : [],
    );
    const [selectedShot, setSelectedShot] = useState(shots[0]);
    const [isLoading, setIsLoading] = useState(!isUpdatedByVariation);
    const [designTab, setDesignTab] = useState(DesignTab.SCENE);
    const sidebar = useRef<HTMLDivElement | null>(null);
    const mainContainer = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        setIsLoading(!isUpdatedByVariation || variations.length === 0);
    }, [isUpdatedByVariation, variations]);

    const getVideoData = useCallback((): { duration: number; frameRate: number } => {
        return {
            duration:
                (creativeModel as MultiPageVideoModel)?.getPlaybackDuration?.()?.getDuration?.() ||
                template.pages[currentPageIndex].duration,
            frameRate: template.pages[currentPageIndex].frameRate,
        };
    }, [creativeModel, currentPageIndex, template]);

    const sceneElements = useMemo(
        () => getVisibleElements(template.pages, currentPageIndex, isDynamicElementSelectionEnabled),
        [template, currentPageIndex, isDynamicElementSelectionEnabled],
    );

    const showPagePreviews = useMemo(() => template.pages.length > 1, [template]);

    const handleShotsReplaced = useCallback(() => {
        if (!(creativeModel instanceof MultiPageVideoModel)) {
            return;
        }

        const updatedShots = creativeModel?.getShots();

        setShots(updatedShots);
        setSelectedShot(updatedShots[selectedShotIndex]);
    }, [creativeModel, selectedShotIndex]);

    const selectShot = useCallback(
        (index: number) => {
            if (index === shots.length || !shots[index]) {
                return;
            }

            canvasRenderer.setCurrentFrame(shots[index].startFrame);

            setSelectedShotIndex(index);
            setSelectedShot(shots[index]);
        },
        [shots, canvasRenderer],
    );

    const toggleToolbarAccess = useCallback((disable) => {
        setIsToolbarDisabled(disable);
    }, []);

    const toNextShot = useCallback(() => {
        if (selectedShotIndex >= shots.length - 1) {
            return;
        }

        if (canvasRenderer.getIsPlaying()) {
            setPausePlayback();
            selectShot(selectedShotIndex + 1);
            setPlayPlayback();
        } else {
            selectShot(selectedShotIndex + 1);
        }
    }, [canvasRenderer, setPausePlayback, setPlayPlayback, selectedShotIndex, selectShot, shots.length]);

    const toPrevShot = useCallback(() => {
        if (canvasRenderer.getIsPlaying()) {
            setPausePlayback();
            selectShot(Math.max(selectedShotIndex - 1, 0));
            setPlayPlayback();
        } else {
            selectShot(Math.max(selectedShotIndex - 1, 0));
        }
    }, [canvasRenderer, setPlayPlayback, selectedShotIndex, selectShot, setPausePlayback]);

    const saveOnLeave = useCallback(() => {
        const currentVariationOBJ = variations?.find((v) => v.id === currentVariation);

        if (!currentVariationOBJ) {
            return;
        }

        const { id, hasUnsavedChanges } = currentVariationOBJ;
        const inProgress = id in variationSaveInProgressMap.current;

        if (inProgress) {
            return;
        }

        if (hasUnsavedChanges) {
            dispatch(pendingDesignSavingRequest(true));
            saveVariation(id);
        } else {
            dispatch(pendingDesignSavingRequest(false));
        }
    }, [variations, currentVariation, saveVariation, dispatch, variationSaveInProgressMap]);

    useEffect(() => {
        setShots(shots);
        setSelectedShot(shots[selectedShotIndex] || shots[0]);
    }, [shots, selectedShotIndex]);

    useEffect(() => {
        if (!creativeModel) {
            return;
        }

        creativeModel.on('shotsReplaced', handleShotsReplaced);
        creativeModel.on('currentPageChange', handleShotsReplaced);

        return () => {
            creativeModel.off('shotsReplaced', handleShotsReplaced);
            creativeModel.off('currentPageChange', handleShotsReplaced);
        };
    }, [creativeModel, handleShotsReplaced]);

    useEffect(() => {
        if (canvasWrapperRef.current) {
            initCanvasRenderer(canvasWrapperRef.current);
        }
    }, [initCanvasRenderer]);

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'hidden') {
                if (isPlaying) {
                    setPausePlayback();
                }

                saveOnLeave();
            }
        };

        window.addEventListener('beforeunload', saveOnLeave);
        document.addEventListener('visibilitychange', handleVisibilityChange); // in case of refresh or close tab

        return () => {
            // dispatch(pendingDesignSavingRequest(true)); remove after QA
            saveOnLeave(); // in case of navigation to another page (our back button)
            window.removeEventListener('beforeunload', saveOnLeave);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [saveOnLeave, isPlaying, setPausePlayback]);

    const handleDimensionResize = useCallback(() => {
        if (document.fullscreenElement) {
            return;
        }

        if (canvasRenderer) {
            canvasRenderer.calculateScaleRatio();
            canvasRenderer.redraw();
        }
    }, [canvasRenderer]);

    const handlePageChangePrevious = useCallback(() => {
        const prevPageIndex = currentPageIndex - 1 >= 0 ? currentPageIndex - 1 : currentPageIndex;
        setCurrentPageIndex(prevPageIndex);
        forceUpdate();
    }, [currentPageIndex, forceUpdate, setCurrentPageIndex]);

    const handlePageChangeNext = useCallback(() => {
        const totalPages = creativeModel?.getModels()?.length;

        if (!totalPages) {
            return;
        }

        const nextPage = currentPageIndex + 1 < totalPages ? currentPageIndex + 1 : currentPageIndex;
        setCurrentPageIndex(nextPage);
        forceUpdate();
    }, [creativeModel, currentPageIndex, forceUpdate, setCurrentPageIndex]);

    usePageShortcuts({
        handlePageChangePrevious,
        handlePageChangeNext,
    });

    useVariationsShortcuts({
        changeToPrevious: selectPreviousVariation,
        changeToNext: selectNextVariation,
    });

    useEffect(() => {
        const resize = debounce(handleDimensionResize, 10, false);
        const resizeObserver = new ResizeObserver(resize);
        resizeObserver.observe(mainContainer.current);

        return () => {
            resizeObserver.disconnect();
        };
    }, [canvasRenderer, handleDimensionResize]);

    return (
        <DesignSelectedElementProvider>
            <DesignWrapper>
                <DesignMain ref={mainContainer}>
                    <PreviewHeader />
                    <DesignZoomControlsWrapper>
                        <CanvasZoomControls renderer={canvasRenderer} />
                    </DesignZoomControlsWrapper>
                    <DesignContainer className="instant--preview-container">
                        <DesignCanvasWrapper creativetype={creativeType}>
                            {showPagePreviews && (
                                <PagePreviewProvider>
                                    <PagePreview />
                                </PagePreviewProvider>
                            )}
                            <DesignCanvasPaddingWrapper showpagepreviews={showPagePreviews.toString()}>
                                <DesignCanvas ref={canvasWrapperRef} className="instant--preview-canvas-wrapper">
                                    <DesignCanvasBorder isloading={isLoading.toString()} ref={borderElementRef}>
                                        {!isUpdatedByVariation && (
                                            <DesignBuffer>
                                                <Buffer />
                                            </DesignBuffer>
                                        )}
                                    </DesignCanvasBorder>
                                </DesignCanvas>
                            </DesignCanvasPaddingWrapper>
                        </DesignCanvasWrapper>
                        {creativeType === CreativeTypes.Video && (
                            <ConfiguratorTimeline
                                designType="CREATIVE"
                                shots={shots}
                                selectedShotIndex={selectedShotIndex}
                                selectShot={selectShot}
                                toNextShot={toNextShot}
                                toPrevShot={toPrevShot}
                                toggleToolbarAccess={toggleToolbarAccess}
                                canvasRenderer={canvasRenderer}
                                setPausePlayback={setPausePlayback}
                                setPlayPlayback={setPlayPlayback}
                                creativeModelFactory={creativeModelFactory}
                                creativeModel={creativeModel}
                                setDesignTab={setDesignTab}
                                hideZoomControl
                                {...getVideoData()}
                            />
                        )}
                    </DesignContainer>
                </DesignMain>
                <CollapseExpandDivider sidebar={sidebar} mainContainer={mainContainer} />
                <DesignSidebar ref={sidebar}>
                    <Sidebar designTab={designTab} setDesignTab={setDesignTab}>
                        {creativeType === 'VIDEO' ? (
                            <ShotVideoDesign
                                template={template}
                                currentPageIndex={currentPageIndex}
                                elements={sceneElements}
                                shots={shots}
                                selectedShot={selectedShot}
                                selectedShotIndex={selectedShotIndex}
                                isToolbarDisabled={isToolbarDisabled}
                                setDesignTab={setDesignTab}
                                isLoading={isLoading}
                            />
                        ) : (
                            <ShotImageDesign
                                elements={sceneElements}
                                isToolbarDisabled={isToolbarDisabled}
                                isLoading={isLoading}
                            />
                        )}
                    </Sidebar>
                </DesignSidebar>
            </DesignWrapper>
        </DesignSelectedElementProvider>
    );
};
